import React, { useState, useCallback, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import { UserRound, Plus } from 'lucide-react';
import { brandSelectTheme } from '../../../Common/reactSelectTheme';

/**
 * Customer block of the invoice editor.
 *
 * One control: the customer select, which keeps showing the chosen customer
 * (change it like any other field). Under it, one muted line with what the
 * invoice will carry — contact and billing address — and one button,
 * "Ändra uppgifter", that opens a modal for per-invoice overrides and for
 * picking among several contacts or addresses.
 *
 * Same props contract as the old CustomerSelectionCard (kept for the
 * credit-note flow).
 */

const addressTypeLabels = {
    billing: 'Fakturaadress',
    shipping: 'Leveransadress',
    invoice: 'Fakturaadress',
    other: 'Annan adress',
};

const selectStyles = {
    control: (base) => ({ ...base, minHeight: 34, borderRadius: 8, borderColor: '#d1d5db', fontSize: '0.875rem' }),
    menu: (base) => ({ ...base, zIndex: 1000 }),
};

const modalSelectStyles = {
    control: (base) => ({ ...base, minHeight: 34, borderRadius: 8, borderColor: '#d1d5db', fontSize: '0.875rem' }),
    menuPortal: (base) => ({ ...base, zIndex: 1060 }),
};

const compact = (parts) => parts.filter(Boolean);

const contactFields = (c) => ({
    contact_name: c?.contact_name || '',
    role: c?.role || '',
    contact_email: c?.contact_email || '',
    contact_phone: c?.contact_phone || '',
});

const addressFields = (a) => ({
    street1: a?.street1 || '',
    street2: a?.street2 || '',
    postal_code: a?.postal_code ?? a?.postalcode ?? '',
    city: a?.city || '',
    country: a?.country || 'Sverige',
});

const sameFields = (a, b) => Object.keys(a).every((key) => (a[key] || '') === (b[key] || ''));

const CustomerCompactCard = ({
    customers = [],
    selectedCustomer,
    onCustomerSelect,
    onFetchCustomerDetails,
    onCreateCustomer,
    selectedAddressId = null,
    selectedContactId = null,
    addressOverride = null,
    contactOverride = null,
    onAddressSelect,
    onContactSelect,
    onAddressOverride,
    onContactOverride,
    onResetAddressOverride,
    onResetContactOverride,
}) => {
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draftContact, setDraftContact] = useState(contactFields(null));
    const [draftAddress, setDraftAddress] = useState(addressFields(null));

    const getCustomerLabel = useCallback((customer) => {
        if (!customer) return '';
        if (customer.type === 'company') {
            const orgNumber = customer.organization_number || customer.orgnumber;
            return `${customer.name}${orgNumber ? ` (${orgNumber})` : ''}`;
        }
        const firstName = customer.first_name || customer.firstName || '';
        const lastName = customer.last_name || customer.lastName || '';
        return customer.name || `${firstName} ${lastName}`.trim();
    }, []);

    const toOption = useCallback(
        (customer) => ({ value: customer.id, label: getCustomerLabel(customer), data: customer }),
        [getCustomerLabel],
    );

    const filterCustomers = useCallback((inputValue) => {
        const term = (inputValue || '').toLowerCase();
        if (!term) return customers;
        return customers.filter((customer) => {
            const name = (customer.name || '').toLowerCase();
            const orgNumber = (customer.organization_number || customer.orgnumber || '').toLowerCase();
            const customerNumber = (customer.customer_number || customer.number || '').toString().toLowerCase();
            const email = (customer.email || '').toLowerCase();
            return name.includes(term) || orgNumber.includes(term)
                || customerNumber.includes(term) || email.includes(term);
        });
    }, [customers]);

    // The list is already in memory; no timer before the dropdown fills.
    const loadCustomerOptions = useCallback(
        (inputValue) => Promise.resolve(filterCustomers(inputValue).slice(0, 50).map(toOption)),
        [filterCustomers, toOption],
    );

    const defaultOptions = useMemo(() => customers.slice(0, 20).map(toOption), [customers, toOption]);

    const handleSelectCustomer = useCallback(async (option) => {
        if (!option) {
            onCustomerSelect(null);
            return;
        }
        const customer = customers.find((c) => c.id === option.value) || option.data;
        if (!customer) return;
        if (onFetchCustomerDetails) {
            setIsLoadingDetails(true);
            try {
                const fullCustomer = await onFetchCustomerDetails(customer.id);
                onCustomerSelect(fullCustomer || customer);
            } catch (error) {
                console.error('Error fetching customer details:', error);
                onCustomerSelect(customer);
            } finally {
                setIsLoadingDetails(false);
            }
        } else {
            onCustomerSelect(customer);
        }
    }, [customers, onFetchCustomerDetails, onCustomerSelect]);

    const activeAddress = useMemo(() => {
        if (addressOverride) return addressOverride;
        const addresses = selectedCustomer?.addresses || [];
        if (!addresses.length) return null;
        if (selectedAddressId) {
            const selected = addresses.find((a) => a.address_id === selectedAddressId);
            if (selected) return selected;
        }
        return addresses.find((a) => a.address_type === 'billing' || a.address_type === 'invoice') || addresses[0];
    }, [selectedCustomer, selectedAddressId, addressOverride]);

    const activeContact = useMemo(() => {
        if (contactOverride) return contactOverride;
        const contacts = selectedCustomer?.contacts || [];
        if (!contacts.length) return null;
        if (selectedContactId) {
            const selected = contacts.find((c) => c.contact_id === selectedContactId);
            if (selected) return selected;
        }
        return contacts[0];
    }, [selectedCustomer, selectedContactId, contactOverride]);

    const addressOptions = useMemo(() => (selectedCustomer?.addresses || []).map((addr) => ({
        value: addr.address_id,
        label: `${addressTypeLabels[addr.address_type] || 'Adress'} – ${compact([addr.street1, addr.city]).join(', ')}`,
    })), [selectedCustomer]);

    const contactOptions = useMemo(() => (selectedCustomer?.contacts || []).map((contact) => ({
        value: contact.contact_id,
        label: `${contact.contact_name}${contact.role ? ` (${contact.role})` : ''}`,
    })), [selectedCustomer]);

    // ── Modal ─────────────────────────────────────────────────────────────
    const openEditor = useCallback(() => {
        setDraftContact(contactFields(activeContact || {
            contact_email: selectedCustomer?.email || '',
            contact_phone: selectedCustomer?.phone || '',
        }));
        setDraftAddress(addressFields(activeAddress));
        setEditing(true);
    }, [activeContact, activeAddress, selectedCustomer]);

    const closeEditor = useCallback(() => setEditing(false), []);

    // Only what actually changed becomes an override, so picking another
    // stored contact/address in the modal does not get frozen as an edit.
    const saveEditor = useCallback(() => {
        const baseContact = contactFields(activeContact || {
            contact_email: selectedCustomer?.email || '',
            contact_phone: selectedCustomer?.phone || '',
        });
        if (!sameFields(draftContact, baseContact)) {
            onContactOverride?.({ ...(activeContact || {}), ...draftContact });
        }
        const baseAddress = addressFields(activeAddress);
        if (!sameFields(draftAddress, baseAddress)) {
            onAddressOverride?.({ ...(activeAddress || {}), ...draftAddress, postalcode: draftAddress.postal_code });
        }
        setEditing(false);
    }, [draftContact, draftAddress, activeContact, activeAddress, selectedCustomer, onContactOverride, onAddressOverride]);

    const pickContact = useCallback((opt) => {
        onContactSelect?.(opt?.value ?? null);
        const contact = (selectedCustomer?.contacts || []).find((c) => c.contact_id === opt?.value);
        setDraftContact(contactFields(contact));
    }, [onContactSelect, selectedCustomer]);

    const pickAddress = useCallback((opt) => {
        onAddressSelect?.(opt?.value ?? null);
        const address = (selectedCustomer?.addresses || []).find((a) => a.address_id === opt?.value);
        setDraftAddress(addressFields(address));
    }, [onAddressSelect, selectedCustomer]);

    const field = (setter) => (key) => (e) => setter((prev) => ({ ...prev, [key]: e.target.value }));
    const setContact = field(setDraftContact);
    const setAddress = field(setDraftAddress);

    // ── Summary line ──────────────────────────────────────────────────────
    const contactSummary = activeContact
        ? compact([activeContact.contact_name, activeContact.contact_email || activeContact.contact_phone])
        : compact([selectedCustomer?.email || selectedCustomer?.phone]);
    const addressSummary = activeAddress
        ? compact([
            activeAddress.street1,
            compact([activeAddress.postal_code || activeAddress.postalcode, activeAddress.city]).join(' '),
            activeAddress.country && activeAddress.country !== 'Sverige' ? activeAddress.country : null,
        ]).join(', ')
        : '';
    const summary = compact([...contactSummary, addressSummary]);
    const hasOverride = Boolean(contactOverride || addressOverride);

    const selectedOption = selectedCustomer ? toOption(selectedCustomer) : null;
    const portalTarget = typeof document !== 'undefined' ? document.body : null;

    return (
        <div className="inv-card">
            <div className="inv-card__head">
                <h3 className="inv-card__title">
                    <UserRound strokeWidth={2} />
                    Kund
                </h3>
                <button type="button" className="inv-btn inv-btn--ghost inv-btn--sm" onClick={onCreateCustomer}>
                    <Plus strokeWidth={2} />
                    Ny kund
                </button>
            </div>

            <AsyncSelect
                inputId="inv-customer-select"
                aria-label="Kund"
                cacheOptions
                defaultOptions={defaultOptions}
                loadOptions={loadCustomerOptions}
                value={selectedOption}
                onChange={handleSelectCustomer}
                isClearable
                placeholder="Sök kund – namn, org.nr, kundnummer eller e-post"
                noOptionsMessage={() => 'Inga kunder hittades'}
                loadingMessage={() => 'Söker…'}
                theme={brandSelectTheme}
                styles={selectStyles}
                classNamePrefix="react-select"
            />

            {isLoadingDetails && (
                <div className="inv-help mt-2" role="status">Hämtar kunduppgifter…</div>
            )}

            {selectedCustomer && !isLoadingDetails && (
                <div className="inv-customer-line">
                    <span className="inv-customer-line__text">
                        {summary.length
                            ? summary.map((part, i) => (
                                <React.Fragment key={`${part}-${i}`}>
                                    {i > 0 && <span className="inv-dot">·</span>}
                                    {part}
                                </React.Fragment>
                            ))
                            : 'Ingen kontakt eller fakturaadress registrerad'}
                        {hasOverride && <span className="inv-badge inv-badge--warn ms-2">Ändrad för denna faktura</span>}
                    </span>
                    <span className="inv-customer-line__actions">
                        {hasOverride && (
                            <button
                                type="button"
                                className="inv-btn inv-btn--ghost inv-btn--sm"
                                onClick={() => { onResetContactOverride?.(); onResetAddressOverride?.(); }}
                            >
                                Återställ
                            </button>
                        )}
                        <button type="button" className="inv-btn inv-btn--ghost inv-btn--sm" onClick={openEditor}>
                            Ändra uppgifter
                        </button>
                    </span>
                </div>
            )}

            <Modal show={editing} onHide={closeEditor} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title as="h5">Uppgifter på fakturan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="inv-help mb-3">
                        Ändringar gäller bara denna faktura. Kundkortet påverkas inte.
                    </p>
                    <div className="inv-modal-cols">
                        <div>
                            <div className="inv-modal-cols__title">Kontakt</div>
                            {contactOptions.length > 1 && (
                                <div className="mb-3">
                                    <label className="inv-label" htmlFor="inv-pick-contact">Välj kontaktperson</label>
                                    <Select
                                        inputId="inv-pick-contact"
                                        options={contactOptions}
                                        value={contactOptions.find((o) => o.value === activeContact?.contact_id) || null}
                                        onChange={pickContact}
                                        isSearchable={false}
                                        theme={brandSelectTheme}
                                        styles={modalSelectStyles}
                                        menuPortalTarget={portalTarget}
                                        classNamePrefix="react-select"
                                    />
                                </div>
                            )}
                            <div className="inv-grid">
                                <div>
                                    <label className="inv-label" htmlFor="inv-edit-contact-name">Namn</label>
                                    <input id="inv-edit-contact-name" className="inv-control" type="text"
                                        value={draftContact.contact_name} onChange={setContact('contact_name')} />
                                </div>
                                <div>
                                    <label className="inv-label" htmlFor="inv-edit-contact-role">Roll</label>
                                    <input id="inv-edit-contact-role" className="inv-control" type="text"
                                        value={draftContact.role} onChange={setContact('role')} />
                                </div>
                                <div>
                                    <label className="inv-label" htmlFor="inv-edit-contact-email">E-post</label>
                                    <input id="inv-edit-contact-email" className="inv-control" type="email"
                                        value={draftContact.contact_email} onChange={setContact('contact_email')} />
                                </div>
                                <div>
                                    <label className="inv-label" htmlFor="inv-edit-contact-phone">Telefon</label>
                                    <input id="inv-edit-contact-phone" className="inv-control" type="tel"
                                        value={draftContact.contact_phone} onChange={setContact('contact_phone')} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="inv-modal-cols__title">Fakturaadress</div>
                            {addressOptions.length > 1 && (
                                <div className="mb-3">
                                    <label className="inv-label" htmlFor="inv-pick-address">Välj adress</label>
                                    <Select
                                        inputId="inv-pick-address"
                                        options={addressOptions}
                                        value={addressOptions.find((o) => o.value === activeAddress?.address_id) || null}
                                        onChange={pickAddress}
                                        isSearchable={false}
                                        theme={brandSelectTheme}
                                        styles={modalSelectStyles}
                                        menuPortalTarget={portalTarget}
                                        classNamePrefix="react-select"
                                    />
                                </div>
                            )}
                            <div className="inv-grid">
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label className="inv-label" htmlFor="inv-edit-street1">Gatuadress</label>
                                    <input id="inv-edit-street1" className="inv-control" type="text"
                                        value={draftAddress.street1} onChange={setAddress('street1')} />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label className="inv-label" htmlFor="inv-edit-street2">
                                        Gatuadress 2<span className="inv-label__hint">valfritt</span>
                                    </label>
                                    <input id="inv-edit-street2" className="inv-control" type="text"
                                        value={draftAddress.street2} onChange={setAddress('street2')} />
                                </div>
                                <div>
                                    <label className="inv-label" htmlFor="inv-edit-postal">Postnummer</label>
                                    <input id="inv-edit-postal" className="inv-control" type="text"
                                        value={draftAddress.postal_code} onChange={setAddress('postal_code')} />
                                </div>
                                <div>
                                    <label className="inv-label" htmlFor="inv-edit-city">Ort</label>
                                    <input id="inv-edit-city" className="inv-control" type="text"
                                        value={draftAddress.city} onChange={setAddress('city')} />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label className="inv-label" htmlFor="inv-edit-country">Land</label>
                                    <input id="inv-edit-country" className="inv-control" type="text"
                                        value={draftAddress.country} onChange={setAddress('country')} />
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="inv-btn inv-btn--secondary" onClick={closeEditor}>Avbryt</button>
                    <button type="button" className="inv-btn inv-btn--primary" onClick={saveEditor}>Använd på fakturan</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CustomerCompactCard;
